import React, { useMemo, useState } from 'react'
import { programMeaning } from '../ofacPrograms'

/**
 * Rich audit-view rendering of a saved Healthcare Search identity card. Modelled after the
 * "Healthcare Check" mock: a dark verdict header, side-by-side exclusion cards, a source-vs-source
 * reconciliation matrix highlighting disagreements, and a sources-queried footer.
 *
 * The whole view is a projection of one CollapsedIdentityCard snapshot — no live re-query — so
 * it reflects exactly what the underwriter saw at save time. That's the point of the audit view:
 * the decision was made against this snapshot and this snapshot survives even if the upstream
 * source data later changes.
 */
// `decision` is what the reviewer did with this match: 'OPEN' (not decided yet), 'DISMISSED' (marked
// clear) or 'CONFIRMED' (record saved). Left out, the card reads as a saved record.
const HealthcareCheckDetail = ({ card, decision }) => {
    if (!card) return null
    const hits = card.hits || []
    const verdict = deriveVerdict(card, decision)
    const exclusions = collectExclusions(hits)
    const badges = deriveBadges(exclusions)
    const consensus = deriveConsensus(hits, card.npi)

    return (
        <div style={{ fontSize: 13, color: '#37414d' }}>
            <VerdictHeader card={card} verdict={verdict} badges={badges} consensus={consensus} />

            {exclusions.length > 0 && (
                <>
                    <SectionLabel>Exclusions on record</SectionLabel>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: exclusions.length > 1 ? 'repeat(auto-fit, minmax(320px, 1fr))' : '1fr',
                        gap: 12,
                        marginBottom: 20,
                    }}>
                        {exclusions.map((e, i) => <ExclusionCard key={i} exclusion={e} />)}
                    </div>
                </>
            )}

            <ReconciliationMatrix hits={hits} card={card} />

            <SourcesFooter hits={hits} />
        </div>
    )
}

// ============================================================ Header

const VerdictHeader = ({ card, verdict, badges, consensus }) => (
    <div style={{
        background: 'var(--neutral-900)',
        color: '#fff',
        padding: '18px 20px',
        borderRadius: 8,
        marginBottom: 20,
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 16,
        alignItems: 'flex-start',
    }}>
        <div>
            <div style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '.16em',
                color: '#8b9db8',
                textTransform: 'uppercase',
                marginBottom: 6,
            }}>
                Healthcare check
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-.01em' }}>
                {card.displayName || '(unknown)'}
            </div>
            <div style={{ fontSize: 12, color: '#c5cfdd', marginTop: 4 }}>
                {card.npi && <>NPI {card.npi}</>}
                {card.credential && <> · {card.credential}</>}
                {card.providerType && <> · {card.providerType}</>}
                {card.state && <> · {card.state}</>}
                {card.dob && <> · DOB {formatDob(card.dob)}</>}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                {badges.sanctionsCount > 0 && (
                    <Badge color='#e07a3d'>OFAC sanctions</Badge>
                )}
                {badges.mandatoryCount > 0 && (
                    <Badge color='#e07a3d'>
                        {badges.mandatoryCount === 1 ? 'Mandatory exclusion' : `${badges.mandatoryCount} mandatory exclusions`}
                    </Badge>
                )}
                {badges.permissiveCount > 0 && (
                    <Badge color='#c9843f'>
                        {badges.permissiveCount === 1 ? 'Permissive exclusion' : `${badges.permissiveCount} permissive exclusions`}
                    </Badge>
                )}
                {consensus.total > 0 && (
                    <Badge color='#3d4d67' subtle>
                        {consensus.agree} of {consensus.total} sources agree on the {consensus.on}
                    </Badge>
                )}
            </div>
        </div>
        <div style={{ textAlign: 'right', minWidth: 220 }}>
            <div style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '.16em',
                color: '#8b9db8',
                textTransform: 'uppercase',
                marginBottom: 6,
            }}>
                Verdict
            </div>
            <div style={{
                fontSize: 24,
                fontWeight: 700,
                color: verdict.color,
                lineHeight: 1.1,
            }}>
                {verdict.text}
            </div>
            <div style={{ fontSize: 12, color: '#c5cfdd', marginTop: 8, lineHeight: 1.4 }}>
                {verdict.description}
            </div>
        </div>
    </div>
)

const Badge = ({ color, subtle, children }) => (
    <span style={{
        display: 'inline-block',
        padding: '3px 12px',
        borderRadius: 20,
        border: `1px solid ${color}`,
        color: subtle ? '#c5cfdd' : color,
        background: subtle ? 'transparent' : 'transparent',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '.02em',
    }}>{children}</span>
)

// ============================================================ Exclusions on record

const ExclusionCard = ({ exclusion }) => {
    const isMandatory = exclusion.severity === 'MANDATORY'
    return (
        <div style={{
            background: '#fff',
            border: '1px solid #e2e6ec',
            borderRadius: 8,
            padding: 16,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--neutral-900)' }}>
                    {exclusion.displayCode}
                </div>
                <span style={{
                    padding: '3px 10px',
                    borderRadius: 3,
                    background: isMandatory ? '#e07a3d' : '#c9843f',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '.05em',
                }}>
                    {exclusion.source === 'OFAC' ? 'SANCTIONS' : exclusion.severity}
                </span>
            </div>
            {exclusion.ofac ? <OfacFacts facts={exclusion.ofac} fallback={exclusion.description} /> : (
                <div style={{ fontSize: 13, color: '#37414d', marginBottom: 14, lineHeight: 1.5 }}>
                    {exclusion.description}
                </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, fontSize: 12 }}>
                <StatCell label='Effective' value={formatShortDate(exclusion.effectiveDate) || '—'} />
                <StatCell label='Authority' value={exclusion.authority || '—'} />
                <StatCell
                    label='Reinstatement'
                    value={exclusion.reinstatementDate ? formatShortDate(exclusion.reinstatementDate) : 'None'}
                    accent={!exclusion.reinstatementDate}
                />
            </div>
        </div>
    )
}

const OfacFacts = ({ facts, fallback }) => {
    const rows = [
        facts.programs.length > 0 && ['Programs', facts.programs
            .map(p => (programMeaning(p) ? `${p} (${programMeaning(p)})` : p)).join(', ')],
        facts.matchedName && ['Matched on', `${facts.matchedNameType ? `${facts.matchedNameType} ` : ''}${facts.matchedName}`],
        facts.title && ['Title', facts.title],
        facts.place && ['Location', facts.place],
        ['Remarks', facts.remarks || (facts.matchedName ? 'None listed' : fallback)],
    ].filter(Boolean)
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 16px', fontSize: 13, color: '#37414d', marginBottom: 14, lineHeight: 1.5 }}>
            {rows.map(([label, value]) => (
                <React.Fragment key={label}>
                    <div style={{ fontWeight: 600, color: 'var(--fg-secondary)' }}>{label}</div>
                    <div style={{ overflowWrap: 'anywhere' }}>{value}</div>
                </React.Fragment>
            ))}
        </div>
    )
}

const StatCell = ({ label, value, accent }) => (
    <div>
        <div style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '.12em',
            color: 'var(--fg-secondary)',
            textTransform: 'uppercase',
            marginBottom: 2,
        }}>
            {label}
        </div>
        <div style={{ fontWeight: 600, color: accent ? '#e07a3d' : 'var(--neutral-900)' }}>{value}</div>
    </div>
)

// ============================================================ Source reconciliation

const RECON_ROWS = [
    { key: 'displayName',   label: 'Name' },
    { key: 'providerType',  label: 'Role' },
    { key: 'address',       label: 'Address' },
    { key: 'effectiveDate', label: 'Effective', format: formatShortDate },
    { key: 'reinstatementDate', label: 'Reinstatement', formatEmpty: 'None' },
    { key: 'specialty',     label: 'Specialty' },
    { key: 'recordStatus',  label: 'Record status' },
]

/**
 * Transpose the hits array into a per-attribute × per-source matrix. Anchor comparison against
 * the NPPES hit when present (NPPES is the identity source-of-truth); flag any cell that
 * disagrees with the anchor, plus the "Differences only" filter hides rows where every cell
 * agrees.
 */
const ReconciliationMatrix = ({ hits, card }) => {
    const [diffOnly, setDiffOnly] = useState(false)
    const columns = useMemo(() => buildColumns(hits), [hits])
    if (columns.length === 0) return null

    // One source has nothing to reconcile against: list its non-empty fields instead.
    if (columns.length === 1) {
        const hit = columns[0].hit
        const fields = RECON_ROWS
            .map(rowDef => ({ rowDef, value: rowDef.format ? rowDef.format(hit[rowDef.key]) : hit[rowDef.key] }))
            .filter(f => f.value)
        if (!fields.length) return null
        return (
            <>
                <SectionLabel>Record details · {columns[0].label}</SectionLabel>
                <div style={{
                    display: 'grid', gridTemplateColumns: '140px 1fr', gap: '6px 16px',
                    marginBottom: 20, fontSize: 12,
                }}>
                    {fields.map(f => (
                        <React.Fragment key={f.rowDef.key}>
                            <div style={{ fontWeight: 600, color: 'var(--neutral-900)' }}>{f.rowDef.label}</div>
                            <div style={{ color: 'var(--neutral-900)' }}>{f.value}</div>
                        </React.Fragment>
                    ))}
                </div>
            </>
        )
    }

    const nppesCol = columns.find(c => c.hit.source === 'NPPES')

    const rows = RECON_ROWS.map(rowDef => {
        const cells = columns.map(col => {
            const raw = col.hit ? col.hit[rowDef.key] : null
            const value = rowDef.format ? rowDef.format(raw) : raw
            return { value, raw }
        })
        const anchor = nppesCol
            ? (nppesCol.hit ? nppesCol.hit[rowDef.key] : null)
            : null
        const hasDiff = anchor != null && cells.some(c => c.raw != null
            && c.raw !== anchor
            && String(c.raw).toUpperCase() !== String(anchor).toUpperCase())
        return { rowDef, cells, hasDiff }
    })

    const visibleRows = diffOnly ? rows.filter(r => r.hasDiff) : rows

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
                <SectionLabel style={{ margin: 0 }}>Source reconciliation</SectionLabel>
                <label style={{ fontSize: 12, color: 'var(--fg-secondary)', cursor: 'pointer', userSelect: 'none' }}>
                    <input
                        type='checkbox'
                        checked={diffOnly}
                        onChange={(e) => setDiffOnly(e.target.checked)}
                        style={{ verticalAlign: 'middle', marginRight: 6 }}
                    />
                    Differences only
                </label>
            </div>
            <div style={{ marginBottom: 20, overflowX: 'auto', border: '1px solid #e2e6ec', borderRadius: 6 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                        <tr style={{ background: 'var(--bg-canvas)' }}>
                            <th style={hdrLeft}>&nbsp;</th>
                            {columns.map((c, i) => (
                                <th key={i} style={hdr}>
                                    {c.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {visibleRows.length === 0 && (
                            <tr>
                                <td colSpan={columns.length + 1}
                                    style={{ padding: 16, textAlign: 'center', color: 'var(--fg-secondary)', fontStyle: 'italic' }}>
                                    All sources agree on every attribute.
                                </td>
                            </tr>
                        )}
                        {visibleRows.map((r, ri) => (
                            <tr key={ri}>
                                <td style={rowLabelCell}>{r.rowDef.label}</td>
                                {r.cells.map((c, ci) => {
                                    const isDiff = r.hasDiff
                                        && nppesCol
                                        && ci !== columns.indexOf(nppesCol)
                                        && c.raw != null
                                        && String(c.raw).toUpperCase() !== String(nppesCol.hit[r.rowDef.key] || '').toUpperCase()
                                    return (
                                        <td key={ci} style={{
                                            ...cellStyle,
                                            background: isDiff ? '#fbe6df' : '#fff',
                                            color: c.value ? 'var(--neutral-900)' : '#a2adbf',
                                        }}>
                                            {c.value || (r.rowDef.formatEmpty || '—')}
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    )
}

const hdr = {
    padding: '10px 12px',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '.12em',
    color: 'var(--fg-secondary)',
    textTransform: 'uppercase',
    textAlign: 'left',
    borderBottom: '1px solid #e2e6ec',
}
const hdrLeft = { ...hdr, width: 140, background: 'var(--bg-canvas)' }
const cellStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #f2f4f7',
    verticalAlign: 'top',
    lineHeight: 1.5,
}
const rowLabelCell = {
    ...cellStyle,
    fontSize: 12,
    fontWeight: 600,
    color: '#37414d',
    background: 'var(--bg-canvas)',
    borderRight: '1px solid #e2e6ec',
    borderBottom: '1px solid #f2f4f7',
}

// ============================================================ Sources footer

const SourcesFooter = ({ hits }) => {
    const summary = useMemo(() => {
        const counts = {}
        for (const h of hits) {
            counts[h.source] = (counts[h.source] || 0) + 1
        }
        return counts
    }, [hits])
    const entries = Object.entries(summary)

    return (
        <div style={{
            marginTop: 20,
            padding: '10px 14px',
            background: 'var(--bg-canvas)',
            borderRadius: 6,
            border: '1px solid #e2e6ec',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 12,
            color: 'var(--fg-secondary)',
            flexWrap: 'wrap',
            gap: 12,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <span style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '.12em',
                    color: 'var(--fg-secondary)',
                    textTransform: 'uppercase',
                }}>
                    Sources queried
                </span>
                {entries.length === 0
                    ? <span>No source hits captured.</span>
                    : entries.map(([src, n]) => (
                        <span key={src}>
                            <span style={{ color: SOURCE_COLOR[src] || 'var(--neutral-900)', fontWeight: 700 }}>●</span>
                            {' '}{SOURCE_LABELS[src] || src} · {n} {n === 1 ? 'hit' : 'hits'}
                        </span>
                    ))
                }
            </div>
            <span style={{ fontStyle: 'italic' }}>
                Snapshot captured at save time; underlying sources may have changed since.
            </span>
        </div>
    )
}

const SOURCE_COLOR = { NPPES: 'var(--rox-lilac-ink)', LEIE: 'var(--status-declined)', SAMGOV: '#6a1b9a', OFAC: '#00695c', STATE_MEDICAID: '#8a4b08' }
const SOURCE_LABELS = { SAMGOV: 'SAM.gov', STATE_MEDICAID: 'State Medicaid' }

// ============================================================ Helpers / derivations

const SectionLabel = ({ children, style }) => (
    <div style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '.12em',
        color: 'var(--fg-secondary)',
        textTransform: 'uppercase',
        margin: '18px 0 8px',
        ...style,
    }}>
        {children}
    </div>
)

/**
 * Map LEIE 1128-series codes + SAM.gov exclusion types into a plain-English description +
 * severity tier. Same statute vocabulary the search-time view uses, but split into a per-
 * exclusion object with an inline description ready to render as a card.
 */
const EXCLUSION_META = {
    '1128a1': { severity: 'MANDATORY',  description: 'Mandatory exclusion — Conviction of a program-related crime under Medicare or Medicaid.' },
    '1128a2': { severity: 'MANDATORY',  description: 'Mandatory exclusion — Conviction relating to patient abuse or neglect.' },
    '1128a3': { severity: 'MANDATORY',  description: 'Mandatory exclusion — Felony conviction for health care fraud.' },
    '1128a4': { severity: 'MANDATORY',  description: 'Mandatory exclusion — Felony conviction relating to controlled substances. Minimum five years; no reinstatement date on file.' },
    '1128b1': { severity: 'PERMISSIVE', description: 'Permissive exclusion — Misdemeanor conviction for health care fraud.' },
    '1128b2': { severity: 'PERMISSIVE', description: 'Permissive exclusion — Conviction relating to obstruction of an investigation.' },
    '1128b3': { severity: 'PERMISSIVE', description: 'Permissive exclusion — Misdemeanor conviction relating to controlled substances.' },
    '1128b4': { severity: 'PERMISSIVE', description: 'Permissive exclusion — Licence revoked, suspended, or surrendered for professional conduct. Runs until the licence is restored by the state board.' },
    '1128b5': { severity: 'PERMISSIVE', description: 'Permissive exclusion — Exclusion or suspension under a federal or state health care program.' },
    '1128b6': { severity: 'PERMISSIVE', description: 'Permissive exclusion — Claims for excessive charges or unnecessary services.' },
    '1128b7': { severity: 'PERMISSIVE', description: 'Permissive exclusion — Fraud, kickbacks, and other prohibited activities.' },
    '1128b8': { severity: 'PERMISSIVE', description: 'Permissive exclusion — Entity controlled by a sanctioned individual.' },
    '1128b14': { severity: 'PERMISSIVE', description: 'Permissive exclusion — Default on health education loan or scholarship obligations.' },
    '1128b15': { severity: 'PERMISSIVE', description: 'Permissive exclusion — Individuals controlling a sanctioned entity.' },
    '1128b16': { severity: 'PERMISSIVE', description: 'Permissive exclusion — Making false statements or misrepresentations.' },
    'Prohibition/Restriction': { severity: 'PERMISSIVE', description: 'Barred from receiving federal contracts, grants, or benefits. Reciprocal effect across agencies.' },
    'Ineligible (Proceedings Pending)': { severity: 'PERMISSIVE', description: 'Excluded while enforcement proceedings are pending. Bar lifts only after the underlying proceeding resolves.' },
}

// Every non-NPPES hit is an exclusion — NPPES is identity, never an exclusion.
const collectExclusions = (hits) => (hits || [])
    .filter(h => h.source !== 'NPPES')
    .map(h => {
        const meta = EXCLUSION_META[h.exclusionType] || {}
        const rawCode = h.exclusionType || ''
        // "1128a4" → "1128(a)(4)"; SAM.gov uses freeform types. OFAC's raw exclusionType is the
        // lowercase SDN_Type ("entity"/"individual"), which isn't a heading — give it a proper
        // "OFAC SDN" label instead (the entity/individual detail lives in the description).
        const m = /^1128([ab])(\d+)$/i.exec(rawCode)
        const displayCode = h.source === 'OFAC'
            ? 'OFAC SDN'
            : h.source === 'STATE_MEDICAID'
                ? (h.excludingAuthority || 'State Medicaid')
                : (m ? `1128(${m[1].toLowerCase()})(${m[2]})` : rawCode)
        // OFAC is a sanctions match (SDN list) — treat as the most serious tier, like a mandatory bar.
        const defaultSeverity = h.source === 'OFAC' ? 'MANDATORY' : 'PERMISSIVE'
        return {
            source: h.source,
            displayCode,
            severity: meta.severity || defaultSeverity,
            description: meta.description
                || (h.reason ? h.reason : `${h.source} record — no plain-English description available for this exclusion type.`),
            authority: h.excludingAuthority,
            effectiveDate: h.effectiveDate,
            reinstatementDate: h.reinstatementDate,
            ofac: h.source === 'OFAC' ? ofacFacts(h) : null,
        }
    })

// What tells an OFAC namesake apart: the programs it's sanctioned under, the name that matched, and
// the remarks (DOB, nationality, ID numbers). Older stored hits only carry "Matched a.k.a.: NAME · remarks".
const ofacFacts = (h) => {
    const m = /^Matched ([^:]+): ([^·]+?)(?:\s·\s(.*))?$/s.exec(h.reason || '')
    return {
        programs: (h.exclusionProgram || '').split(/[[\];,]+/).map(p => p.trim()).filter(Boolean),
        matchedName: h.matchedName || (m ? m[2].trim() : null),
        matchedNameType: h.matchedNameType || (m ? m[1] : null),
        remarks: h.remarks || (m ? m[3] || null : null),
        title: h.title || null,
        place: [h.city, h.country].filter(Boolean).join(', ') || null,
    }
}

// OFAC is a sanctions list, not a health care exclusion, so it gets its own badge.
const deriveBadges = (exclusions) => ({
    sanctionsCount: exclusions.filter(e => e.source === 'OFAC').length,
    mandatoryCount: exclusions.filter(e => e.source !== 'OFAC' && e.severity === 'MANDATORY').length,
    permissiveCount: exclusions.filter(e => e.source !== 'OFAC' && e.severity === 'PERMISSIVE').length,
})

const deriveConsensus = (hits, cardNpi) => {
    // Counted per list (source), not per record: one list can return several rows for the same person.
    const npiHits = new Set((hits || []).filter(h => h.npi && h.npi === cardNpi).map(h => h.source)).size
    const total = new Set((hits || []).map(h => h.source)).size
    if (cardNpi) return { agree: npiHits, total, on: 'NPI' }
    return { agree: total, total, on: 'name' }
}

export const deriveVerdict = (card, decision) => {
    const onList = card.status === 'EXCLUDED' || card.status === 'PRIOR_SANCTIONS'
    // Only a saved record gets a funding verdict; a match nobody has checked yet is just a possibility.
    if (onList && decision === 'OPEN') {
        return {
            text: 'Possible match',
            color: '#e5b352',
            description: 'Not confirmed yet. Save the record if this is them.',
        }
    }
    if (onList && decision === 'DISMISSED') {
        return {
            text: 'Marked clear',
            color: '#7bd3a2',
            description: 'Ruled out: not the person or business being checked.',
        }
    }
    const ofac = (card.hits || []).some(h => h.source === 'OFAC')
    switch (card.status) {
        case 'EXCLUDED':
            return {
                text: 'Do not fund',
                color: '#ff6a3d',
                description: ofac
                    ? 'On the US Treasury OFAC sanctions list. Doing business with them is prohibited.'
                    : 'Excluded from federal health care programs. Claims billed by this provider are not payable and cannot be reimbursed.',
            }
        case 'PRIOR_SANCTIONS':
            return {
                text: 'Fund with review',
                color: '#e5b352',
                description: 'Prior sanctions on file — currently reinstated. Escalate to an underwriter for a manual read of the reinstatement history.',
            }
        default:
            return {
                text: 'No exclusions found',
                color: '#7bd3a2',
                description: 'Identity confirmed against the NPI directory; no active or prior exclusion records surfaced across the sources checked.',
            }
    }
}

const buildColumns = (hits) => {
    const cols = []
    let leieIdx = 0
    for (const h of hits) {
        let label = h.source
        if (h.source === 'LEIE') {
            leieIdx += 1
            const code = h.exclusionType || ''
            const m = /^1128([ab])(\d+)$/i.exec(code)
            label = m ? `LEIE · ${m[1].toUpperCase()}${m[2]}` : `LEIE · ${leieIdx}`
        } else if (h.source === 'SAMGOV') {
            label = 'SAM.gov'
        } else if (h.source === 'OFAC') {
            label = 'OFAC'
        } else if (h.source === 'STATE_MEDICAID') {
            label = h.excludingAuthority || 'State Medicaid'
        }
        cols.push({ label, hit: h })
    }
    return cols
}

function formatShortDate(d) {
    if (!d) return d
    if (/^\d{8}$/.test(d)) return `${d.slice(6,8)} ${monthName(d.slice(4,6))} ${d.slice(0,4)}`
    // ISO 2020-02-12 → 12 Feb 2020
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(d)
    if (iso) return `${iso[3]} ${monthName(iso[2])} ${iso[1]}`
    return d
}

function monthName(mm) {
    return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][Math.max(0, parseInt(mm, 10) - 1)]
}

function formatDob(d) {
    if (!d) return d
    if (/^\d{8}$/.test(d)) return `${d.slice(6,8)} ${monthName(d.slice(4,6))} ${d.slice(0,4)}`
    return d
}

export default HealthcareCheckDetail
